<script setup lang="ts">
import { ref, watch, onMounted, defineProps } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart, GaugeChart } from 'echarts/charts'
import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  MarkLineComponent,
  MarkPointComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'

// Registrar componentes de ECharts
use([
  CanvasRenderer,
  LineChart,
  GaugeChart,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  MarkLineComponent,
  MarkPointComponent,
])

// Interface para los datos de batería
interface BatteryReading {
  timestamp: string
  batteryLevel: number  // Porcentaje de batería (0-100)
  voltage: number       // Voltaje de la batería
}

// Props del componente
const props = defineProps<{
  batteryData: BatteryReading[]
  title?: string
  height?: string
  showVoltage?: boolean
}>()

// Configuración del gráfico
const option = ref({
  title: {
    text: props.title || 'Monitoreo de Batería',
    left: 'center',
    textStyle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: '#333'
    }
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      crossStyle: {
        color: '#999'
      }
    },
    formatter: function(params: Array<{axisValue: string, color: string, seriesName: string, value: number}>) {
      if (!params || params.length === 0) return ''
      
      const timestamp = params[0].axisValue
      const date = new Date(timestamp)
      let tooltipContent = `
        <div style="padding: 8px;">
          <div style="font-weight: bold; margin-bottom: 8px; color: #333;">
            ${date.toLocaleDateString('es-ES', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
      `
      
      params.forEach((param: {axisValue: string, color: string, seriesName: string, value: number}) => {
        const color = param.color
        const seriesName = param.seriesName
        const value = param.value
        
        if (seriesName === 'Nivel de Batería') {
          tooltipContent += `
            <div style="display: flex; align-items: center; margin-bottom: 4px;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${color}; border-radius: 50%; margin-right: 8px;"></span>
              <span style="font-weight: 500;">${seriesName}:</span>
              <span style="margin-left: 8px; font-weight: bold; color: ${color};">${value}%</span>
            </div>
          `
        } else if (seriesName === 'Voltaje') {
          tooltipContent += `
            <div style="display: flex; align-items: center; margin-bottom: 4px;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${color}; border-radius: 50%; margin-right: 8px;"></span>
              <span style="font-weight: 500;">${seriesName}:</span>
              <span style="margin-left: 8px; font-weight: bold; color: ${color};">${value}V</span>
            </div>
          `
        }
      })
      
      tooltipContent += '</div>'
      return tooltipContent
    }
  },
  legend: {
    top: '8%',
    data: props.showVoltage ? ['Nivel de Batería', 'Voltaje'] : ['Nivel de Batería']
  },
  grid: {
    left: '3%',
    right: props.showVoltage ? '8%' : '4%',
    bottom: '15%',
    top: '15%',
    containLabel: true
  },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: [] as string[],
    axisLabel: {
      rotate: 45,
      fontSize: 10,
      formatter: (value: string) => {
        const date = new Date(value)
        return date.toLocaleDateString('es-ES', { 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    }
  },
  yAxis: [
    {
      type: 'value',
      name: 'Batería (%)',
      position: 'left',
      min: 0,
      max: 100,
      axisLabel: {
        formatter: '{value}%',
        color: '#4CAF50'
      },
      axisLine: {
        lineStyle: {
          color: '#4CAF50'
        }
      },
      splitLine: {
        lineStyle: {
          color: '#f0f0f0',
          type: 'dashed'
        }
      }
    }
  ],
  dataZoom: [
    {
      type: 'slider',
      show: true,
      xAxisIndex: [0],
      start: 0,
      end: 100,
      height: 20,
      bottom: 5
    }
  ],
  series: [
    {
      name: 'Nivel de Batería',
      type: 'line',
      yAxisIndex: 0,
      data: [] as number[],
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      lineStyle: {
        color: '#4CAF50',
        width: 4
      },
      itemStyle: {
        color: '#4CAF50',
        borderWidth: 2,
        borderColor: '#fff'
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(76, 175, 80, 0.4)' },
            { offset: 0.5, color: 'rgba(76, 175, 80, 0.2)' },
            { offset: 1, color: 'rgba(76, 175, 80, 0.05)' }
          ]
        }
      },
      markLine: {
        data: [
          {
            yAxis: 20,
            name: 'Batería Baja',
            lineStyle: {
              color: '#FF5722',
              type: 'dashed',
              width: 2
            },
            label: {
              formatter: 'Batería Baja (20%)',
              position: 'end'
            }
          },
          {
            yAxis: 10,
            name: 'Batería Crítica',
            lineStyle: {
              color: '#F44336',
              type: 'solid',
              width: 3
            },
            label: {
              formatter: 'Batería Crítica (10%)',
              position: 'end'
            }
          }
        ]
      },
      markPoint: {
        data: [
          { type: 'max', name: 'Máximo' },
          { type: 'min', name: 'Mínimo' }
        ],
        itemStyle: {
          color: '#4CAF50'
        }
      }
    }
  ]
})

// Agregar eje Y y serie para voltaje si está habilitado
if (props.showVoltage) {
  option.value.yAxis.push({
    type: 'value',
    name: 'Voltaje (V)',
    position: 'right',
    min: 0,
    max: 12,
    axisLabel: {
      formatter: '{value}V',
      color: '#FF9800'
    },
    axisLine: {
      lineStyle: {
        color: '#FF9800'
      }
    },
    splitLine: {
      lineStyle: {
        color: '#f0f0f0',
        type: 'dashed'
      }
    }
  })

  const voltageSeries = {
    ...option.value.series[0],
    name: 'Voltaje',
    yAxisIndex: 1,
    data: [] as number[],
    lineStyle: {
      color: '#FF9800',
      width: 3
    },
    itemStyle: {
      color: '#FF9800',
      borderWidth: 1,
      borderColor: '#fff'
    },
    symbol: 'diamond',
    symbolSize: 6,
    areaStyle: {
      color: {
        type: 'linear',
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
          { offset: 0, color: 'rgba(255, 152, 0, 0.1)' },
          { offset: 0.5, color: 'rgba(255, 152, 0, 0.05)' },
          { offset: 1, color: 'rgba(255, 152, 0, 0.02)' }
        ]
      }
    },
    markPoint: {
      data: [],
      itemStyle: {
        color: '#FF9800'
      }
    },
    markLine: {
      data: [],
      lineStyle: {
        color: '#FF9800',
        type: 'dashed'
      }
    }
  }

  option.value.series.push(voltageSeries)
}

const chartRef = ref<InstanceType<typeof VChart> | null>(null)

const updateChartData = (data: BatteryReading[]) => {
  const timestamps = data.map(item => item.timestamp)
  const batteryLevels = data.map(item => item.batteryLevel)
  const voltages = data.map(item => item.voltage)

  option.value.xAxis.data = timestamps
  option.value.series[0].data = batteryLevels

  // Actualizar datos de voltaje si está habilitado
  if (props.showVoltage && option.value.series.length > 1) {
    option.value.series[1].data = voltages
  }
}

// Watchers para actualizar datos
watch(() => props.batteryData, (newData) => {
  if (newData && newData.length > 0) {
    updateChartData(newData)
  }
}, { immediate: true, deep: true })

// Inicializar datos al montar
onMounted(() => {
  if (props.batteryData && props.batteryData.length > 0) {
    updateChartData(props.batteryData)
  }
})
</script>

<template>
  <div class="battery-chart-container">
    <VChart
      ref="chartRef"
      :option="option"
      :style="{ height: height || '350px', width: '100%' }"
      autoresize
    />
  </div>
</template>

<style scoped>
.battery-chart-container {
  width: 100%;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
}
</style>
